import { purchasesStore } from "@stores/purchases";
import { ReactElement, useEffect, useMemo } from "react";
import { IPlanEstimateProps } from "./types";
import { PurchaseContextProvider, usePurchaseContext } from "@addons/hooks";
import { getDescriptor } from "@addons/registry";
import { AddOnEstimateRow } from "@addons/components/EstimateRow";
import ErrorMessage from "@common/components/text/ErrorMessage";
import {
  Anchor,
  Box,
  Divider,
  Flex,
  Group,
  Skeleton,
  Stack,
  Text,
  useMantineTheme,
} from "@mantine/core";
import ResponsiveLineReturn from "@common/components/text/ResponsiveLineReturn";
import { formatPrice } from "@common/utils/string";
import { formatDate } from "@utils/date";
import { usePlan } from "@stores/plans/hooks";
import { hasAddOnsChanges } from "./utils";
import WarningCard from "@common/components/thirdParty/mantine/card/WarningCard";
import { SUBSCRIPTION_STATUS_TRIAL } from "@stores/subscriptions/constants";
import { AddOnProductType } from "@stores/addons/types";

const canRender = (type: AddOnProductType) =>
  !!getDescriptor(type)?.getEstimateRowItem;
// the rows read the add-on context, and half the callers sit outside one
export default function PlanEstimate(props: IPlanEstimateProps): ReactElement {
  return (
    <PurchaseContextProvider purchase={props.purchase}>
      <Estimate {...props} />
    </PurchaseContextProvider>
  );
}

function Estimate({
  changesData,
  withDivider = true,
  ...stackProps
}: Omit<IPlanEstimateProps, "purchase">): ReactElement {
  const theme = useMantineTheme();

  const { isYearly, purchase, subscribedByType } = usePurchaseContext();
  const { subscription, product } = purchase;
  const billingFrequencyText = isYearly ? "year" : "month";
  const upsertAddOns = useMemo(
    () =>
      changesData.upsert_add_ons?.map(({ product_type }) => product_type) || [],
    [changesData],
  );
  const removeAddOns = useMemo(
    () =>
      changesData.remove_add_ons?.map(({ product_type }) => product_type) || [],
    [changesData],
  );

  // plan
  const plan = usePlan({ alias: changesData.new_plan_alias });

  // current add-ons list
  const subscribedAddOns = useMemo(() => {
    const changedAddOns = new Set(upsertAddOns.concat(removeAddOns));

    return Object.values(subscribedByType).filter(
      ({ product_type }) =>
        !changedAddOns.has(product_type) && canRender(product_type),
    );
  }, [subscribedByType, removeAddOns, upsertAddOns]);

  // new add-ons
  const displayUpsertAddOns = useMemo(
    () =>
      changesData.upsert_add_ons?.filter((addOn) =>
        canRender(addOn.product_type),
      ),
    [changesData],
  );

  // Next amount?
  const displayNextAmount = !(
    subscription.status !== SUBSCRIPTION_STATUS_TRIAL &&
    hasAddOnsChanges(subscription, changesData)
  );

  // estimate
  const {
    isPending: loadingGetPlanChangeEstimate,
    error: getPlanChangeEstimateError,
    mutate: getPlanChangeEstimate,
    data: planChangeEstimate,
  } = purchasesStore.useGetPlanChangeEstimate(purchase.id);

  useEffect(() => {
    if (displayNextAmount && plan) {
      getPlanChangeEstimate(changesData);
    }
  }, [displayNextAmount, plan, getPlanChangeEstimate, changesData]);

  if (!plan) {
    return (
      <WarningCard withWarningIcon>
        <Text fz="sm">
          Estimate not available, please contact{" "}
          <Anchor href="mailto:help@metabase.com">help@metabase.com</Anchor>.
        </Text>
      </WarningCard>
    );
  }

  // add-ons only changes
  if (!displayNextAmount) {
    return (
      <Box>
        {!!displayUpsertAddOns?.length &&
          displayUpsertAddOns.map((addOn) => (
            <AddOnEstimateRow key={addOn.product_type} addOn={addOn} />
          ))}
        <Text fz="xs" fs="italic">
          Additional amount for the add-on will be added
          <ResponsiveLineReturn />
          to your next billing period invoice.
        </Text>
      </Box>
    );
  }

  return (
    <Stack spacing="sm" w="100%" {...stackProps}>
      {withDivider && <Divider color="gray.3" />}

      {getPlanChangeEstimateError ? (
        <ErrorMessage error={getPlanChangeEstimateError} />
      ) : (
        <Stack spacing="xs">
          <Box w="100%">
            {plan.alias !== product.plan.alias ? (
              <>
                <Flex
                  align="center"
                  direction="row"
                  gap="xl"
                  justify="space-between"
                  wrap="nowrap"
                  w="100%"
                >
                  <Text fz="xs">
                    <Text span>
                      {plan.short_name || plan.billing_details_name}
                    </Text>
                    <ResponsiveLineReturn />
                    (your new plan)
                  </Text>
                  <Text fz="xs">
                    {formatPrice(plan.price)}/{billingFrequencyText}
                  </Text>
                </Flex>
                <Flex
                  align="center"
                  direction="row"
                  gap="xl"
                  justify="space-between"
                  wrap="nowrap"
                  w="100%"
                >
                  <Text fz="xs">Users ({plan.users_included} included)</Text>
                  <Text fz="xs">
                    {formatPrice(plan.per_user_price)}/{billingFrequencyText}
                  </Text>
                </Flex>
              </>
            ) : (
              <>
                <Flex
                  align="center"
                  direction="row"
                  gap="xl"
                  justify="space-between"
                  wrap="nowrap"
                  w="100%"
                >
                  <Text fz="xs">
                    <Text span>
                      {product.plan.short_name ||
                        product.plan.billing_details_name}
                    </Text>
                    <ResponsiveLineReturn />
                    (your current plan)
                  </Text>
                  <Text fz="xs">
                    {product.plan.price}/{billingFrequencyText}
                  </Text>
                </Flex>
                <Flex
                  align="center"
                  direction="row"
                  gap="xl"
                  justify="space-between"
                  wrap="nowrap"
                  w="100%"
                >
                  <Text fz="xs">
                    Users ({product.plan.users_included} included)
                  </Text>
                  <Text fz="xs">
                    {product.plan.per_user_price}/{billingFrequencyText}
                  </Text>
                </Flex>
              </>
            )}
          </Box>

          {!!subscribedAddOns.length &&
            subscribedAddOns.map((subscriptionAddOn) => (
              <AddOnEstimateRow
                key={subscriptionAddOn.product_type}
                addOn={subscriptionAddOn}
              />
            ))}

          {!!displayUpsertAddOns?.length &&
            displayUpsertAddOns.map((upsertAddOn) => (
              <AddOnEstimateRow
                key={upsertAddOn.product_type}
                addOn={upsertAddOn}
              />
            ))}

          <Flex
            align="center"
            direction="row"
            gap="xl"
            justify="space-between"
            wrap="nowrap"
            w="100%"
          >
            <Text fz="xs">
              Estimated next payment
              {planChangeEstimate && (
                <>
                  <ResponsiveLineReturn />
                  <Text span>
                    ({formatDate(planChangeEstimate.next_payment_date, "long")})
                  </Text>
                </>
              )}
            </Text>
            <Group spacing="xs">
              {planChangeEstimate && (
                <Text fz="xs">
                  {formatPrice(planChangeEstimate.next_payment_amount / 100)}
                </Text>
              )}
              {loadingGetPlanChangeEstimate && (
                <Skeleton
                  height={theme.fontSizes.xs}
                  radius="xs"
                  width={75 + 50 * Math.random()}
                />
              )}
            </Group>
          </Flex>

          <Flex
            align="center"
            direction="row"
            gap="xl"
            justify="space-between"
            wrap="nowrap"
            w="100%"
          >
            <Text c="gray.6" fw={700}>
              Total due today
            </Text>
            <Group spacing="xs">
              {planChangeEstimate && (
                <Text c="gray.6" fw={700}>
                  {formatPrice(planChangeEstimate.amount_due_now / 100)}
                </Text>
              )}
              {loadingGetPlanChangeEstimate && (
                <Skeleton
                  height={theme.fontSizes.md}
                  radius="sm"
                  width={75 + 50 * Math.random()}
                />
              )}
            </Group>
          </Flex>
        </Stack>
      )}
    </Stack>
  );
}
